// Builds the page from data. Text goes in as text nodes, never as HTML, so nothing from an email, a document or a
// draft can become markup.

type Child = Node | string | number | null | undefined | false | Child[];
type Attrs = Record<string, string | number | boolean | null | undefined | EventListener>;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs | null = null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs ?? {})) {
    if (v === null || v === undefined || v === false) continue;
    if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (v === true) el.setAttribute(k, "");
    else el.setAttribute(k, String(v));
  }
  append(el, children);
  return el;
}

function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else el.appendChild(typeof c === "string" || typeof c === "number" ? document.createTextNode(String(c)) : c);
  }
}

export function replace(el: Element, ...children: Child[]): void {
  el.replaceChildren();
  append(el, children);
}

/** A status pill: always a word, never colour alone. */
export function pill(text: string, tone: "late" | "soon" | "good" | "neutral" = "neutral"): HTMLElement {
  return h("span", { class: `pill pill-${tone}` }, text);
}

/** A panel heading with an optional note on the right. */
export function sectionHead(id: string, title: string, note?: string): HTMLElement {
  return h("div", { class: "section-head" }, h("h2", { id, class: "heading" }, title), note ? h("span", { class: "note" }, note) : null);
}

/** A dashed "nothing here" box. */
export function empty(title: string, text: string): HTMLElement {
  return h("div", { class: "empty" }, h("span", { class: "subheading" }, title), h("span", null, text));
}

export function label(text: string): HTMLElement {
  return h("span", { class: "label" }, text);
}
